import { cn } from "@/lib/utils";
import type { UIMessage } from "ai";
import {
  Check,
  Copy,
  RefreshCw,
  ThumbsDown,
  ThumbsUp,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { buildSupervisorMessageCopyText } from "../lib/message-copy-text";

function formatMessageTimestamp(date: Date | undefined): string | null {
  if (!date || Number.isNaN(date.getTime())) return null;
  const now = new Date();
  const sameDay =
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate();
  if (sameDay) {
    return date.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  }
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function messageCreatedAt(message: UIMessage): Date | undefined {
  const meta = message.metadata;
  if (meta && typeof meta === "object" && "createdAt" in meta) {
    const raw = (meta as { createdAt?: unknown }).createdAt;
    if (raw instanceof Date) return raw;
    if (typeof raw === "string" || typeof raw === "number") {
      const date = new Date(raw);
      if (!Number.isNaN(date.getTime())) return date;
    }
  }
  return undefined;
}

const actionButtonClassName =
  "inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md border-0 bg-transparent text-[#576274] transition-colors hover:bg-[var(--lm-hover)] hover:text-[#101828] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400/40 disabled:pointer-events-none disabled:opacity-50 data-[copied=true]:text-[#7c3aed] data-[submitted=true]:bg-violet-500/15 data-[submitted=true]:text-[#7c3aed] dark:text-slate-400 dark:hover:bg-white/10 dark:hover:text-white dark:data-[copied=true]:text-violet-300 dark:data-[submitted=true]:bg-violet-500/20 dark:data-[submitted=true]:text-violet-200 [&_svg]:pointer-events-none [&_svg]:size-3.5";

const copyToClipboard = (text: string) => {
  if (typeof navigator === "undefined" || !navigator.clipboard) {
    return Promise.reject(new Error("Clipboard API is unavailable"));
  }
  return navigator.clipboard.writeText(text);
};

/**
 * Message footer: timestamp + copy / karma / regenerate.
 * Pure React — no assistant-ui store (avoids getSnapshot update-depth loops).
 */
export function SupervisorMessageActions({
  message,
  variant,
  busy = false,
  onRegenerate,
  showFeedback = true,
}: {
  message: UIMessage;
  variant: "user" | "assistant";
  busy?: boolean;
  onRegenerate?: () => void;
  /** Local-only thumbs; no persistence backend yet. */
  showFeedback?: boolean;
}) {
  const messageId = message.id;
  const [localFeedback, setLocalFeedback] = useState<"positive" | "negative" | null>(null);
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    setLocalFeedback(null);
    setIsCopied(false);
  }, [messageId]);

  const copyText = useMemo(
    () => buildSupervisorMessageCopyText(message, variant),
    [message, variant],
  );

  const onCopy = useCallback(() => {
    if (!copyText) return;
    void copyToClipboard(copyText)
      .then(() => {
        setIsCopied(true);
        window.setTimeout(() => setIsCopied(false), 2000);
      })
      .catch(() => {
        /* clipboard unavailable */
      });
  }, [copyText]);

  const timeLabel = formatMessageTimestamp(messageCreatedAt(message));
  const copyDisabled = busy || !copyText;
  const reloadDisabled = busy || !onRegenerate;

  return (
    <div
      className={cn(
        "mt-1.5 flex items-center gap-2 px-0.5",
        variant === "user" ? "flex-row-reverse" : "flex-row",
      )}
      data-slot="supervisor-message-actions"
    >
      <span className="min-h-[14px] text-[11px] tabular-nums text-[#576274] dark:text-slate-400">
        {timeLabel || "\u00a0"}
      </span>
      <div
        className={cn(
          "flex items-center gap-0.5 text-[#576274] dark:text-slate-400",
          variant === "user" && "flex-row-reverse",
        )}
      >
        <button
          type="button"
          title="Copy"
          aria-label="Copy message"
          className={actionButtonClassName}
          disabled={copyDisabled}
          data-copied={isCopied ? "true" : undefined}
          onClick={onCopy}
        >
          {isCopied ? <Check /> : <Copy />}
        </button>

        {variant === "assistant" ? (
          <>
            {showFeedback ? (
              <>
                <button
                  type="button"
                  title="Helpful"
                  aria-label="Mark helpful"
                  className={actionButtonClassName}
                  data-submitted={localFeedback === "positive" ? "true" : undefined}
                  onClick={() => setLocalFeedback("positive")}
                >
                  <ThumbsUp />
                </button>
                <button
                  type="button"
                  title="Not helpful"
                  aria-label="Mark not helpful"
                  className={actionButtonClassName}
                  data-submitted={localFeedback === "negative" ? "true" : undefined}
                  onClick={() => setLocalFeedback("negative")}
                >
                  <ThumbsDown />
                </button>
              </>
            ) : null}
            {onRegenerate ? (
              <button
                type="button"
                title="Regenerate"
                aria-label="Regenerate response"
                className={actionButtonClassName}
                disabled={reloadDisabled}
                onClick={onRegenerate}
              >
                <RefreshCw />
              </button>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
}
